import { BotSequenceConfigEffectiveField, BotSequenceLegacyEnabledLocation, BotSequenceMode, LocationVarInheritance } from "@swivl-mono/types";
import { Toggle, ToggleOption } from "@swivl-mono/ui";

import { CortexInheritancePill } from "../../CortexV3/Components/CortexInheritancePill";
import { RowInheritanceControls } from "../../CortexV3/Components/RowInheritanceControls";
import { REVERT_TO_DEFAULT } from "../../CortexV3/Components/revertTarget";
import { FieldState } from "../Hooks/useSequenceConfig";
import { SequenceScopeKind } from "../sequenceScope";
import { describeLegacyEnabledHint } from "./legacyEnabledHint";

interface ModeSectionProps {
  effective: BotSequenceConfigEffectiveField<BotSequenceMode | null>;
  staged: FieldState<BotSequenceMode>;
  /** ENG-3512: non-empty = the API refuses ENABLED at this scope, so "On" is disabled with a hint. */
  legacyEnabledLocations: BotSequenceLegacyEnabledLocation[];
  scopeKind: SequenceScopeKind;
  canEdit: boolean;
  onChange: (mode: BotSequenceMode) => void;
  onRevert: () => void;
  onDiscard: () => void;
}

const OPTIONS: ToggleOption[] = [
  { value: BotSequenceMode.ENABLED, label: "On" },
  { value: BotSequenceMode.SIMULATED, label: "Simulated" },
  { value: BotSequenceMode.DISABLED, label: "Off" }
];

export function inheritanceFor(source: "location" | "group" | "org" | "default", scopeKind: SequenceScopeKind): LocationVarInheritance {
  if (source === "default") return LocationVarInheritance.DEFAULT;
  if (source === scopeKind) return LocationVarInheritance.NONE;
  return source === "group" ? LocationVarInheritance.GROUP : LocationVarInheritance.ORG;
}

const MODE_CAPTIONS: Record<BotSequenceMode, string> = {
  [BotSequenceMode.ENABLED]: "On - days are booked and sent at this scope.",
  [BotSequenceMode.SIMULATED]: "Simulated logs every decision as if live and sends nothing.",
  [BotSequenceMode.DISABLED]: "Off - nothing is booked for this type."
};

/** On / Simulated / Off for everyone (ENG-3216 decision 19), with the inheritance pill and Customize / Revert. */
export function ModeSection({
  effective,
  staged,
  legacyEnabledLocations,
  scopeKind,
  canEdit,
  onChange,
  onRevert,
  onDiscard
}: ModeSectionProps): React.ReactElement {
  // Nothing saved at any scope is the default, Off — shown as such (first-class defaults, decision 56).
  const resolved = staged.kind === "set" ? staged.value : staged.kind === "revert" ? null : effective.value;
  const shown: BotSequenceMode = resolved ?? BotSequenceMode.DISABLED;
  const inheritance = inheritanceFor(effective.source, scopeKind);
  // ENG-3512: "On" stays shown when already ENABLED (edited outside the UI) but cannot be re-selected.
  const legacyHint = describeLegacyEnabledHint(legacyEnabledLocations);
  const options = legacyHint
    ? OPTIONS.map((option) => (option.value === BotSequenceMode.ENABLED ? { ...option, disabled: true, title: legacyHint } : option))
    : OPTIONS;

  const handleOptionSelected = (option: ToggleOption): void => {
    if (option.value) onChange(option.value as BotSequenceMode);
  };

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="body-sm-alt text-grey-900">Sequence</div>
          <div className="body-sm text-grey-600">{MODE_CAPTIONS[shown]}</div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {canEdit ? (
            <RowInheritanceControls
              inheritance={inheritance}
              overrideStaged={staged.kind === "set"}
              showCustomize={false}
              revertLabel={scopeKind === "org" ? REVERT_TO_DEFAULT : undefined}
              onCustomize={() => undefined}
              onRevert={onRevert}
              onDiscard={onDiscard}
            />
          ) : (
            <CortexInheritancePill inheritance={inheritance} />
          )}
          <Toggle options={options} selectedOption={shown} onOptionSelected={handleOptionSelected} readOnly={!canEdit} />
        </div>
      </div>
      {staged.kind === "revert" && (
        <p className="body-xs border-t border-grey-200 px-4 py-2 text-grey-600">Reverts to the inherited setting when you save.</p>
      )}
    </section>
  );
}
